import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  LoadDataAction,
  EntityState,
  DataPayload,
  SortPayloadAction,
} from '../types';
import { AxiosInstance } from 'axios';
import { JourneyFilters, JourneyEntity, HoveredSnapshot } from './types';
import { structuredFetch } from '../../api/api';
import { Auth } from '../auth/types';
import { ShipmentEntity } from '../shipments/types';
import { ArtycSnapshotMode } from '../segments/types';
import { EventTypesEnum } from '../events/types';

export interface JourneyState extends EntityState<JourneyEntity> {
  selectedFilters?: JourneyFilters;
  viewAsCustomer: boolean;
  shipmentsByJourney: Record<
    JourneyEntity['_id'],
    ShipmentEntity[] | undefined
  >;
  hoveredSnapshot: HoveredSnapshot | null;
  artycSnapshotsMode: ArtycSnapshotMode;
}

// Persist the view-as-customer toggle so it survives reloads and new tabs.
const VIEW_AS_CUSTOMER_KEY = 'artyc.viewAsCustomer';
const readPersistedViewAsCustomer = (): boolean => {
  try {
    return localStorage.getItem(VIEW_AS_CUSTOMER_KEY) === 'true';
  } catch {
    return false;
  }
};
const persistViewAsCustomer = (value: boolean) => {
  try {
    localStorage.setItem(VIEW_AS_CUSTOMER_KEY, String(value));
  } catch {
    /* ignore */
  }
};

const initialState: JourneyState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 10,
  totalPages: 0,
  sortColumn: 'createdAt',
  sortDirection: -1,
  selectedFilters: {},
  viewAsCustomer: readPersistedViewAsCustomer(),
  shipmentsByJourney: {},
  hoveredSnapshot: null,
  artycSnapshotsMode: 'all',
};

const journeySlice = createSlice({
  name: 'journeys',
  initialState,
  reducers: {
    setJourneys: (
      state,
      { payload }: PayloadAction<DataPayload<JourneyEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    sortJourneys: (state, { payload }: SortPayloadAction<JourneyEntity>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    setDeviceTypesFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        deviceTypes: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setDateFilter: (
      state,
      { payload }: PayloadAction<[string | undefined, string | undefined]>
    ) => {
      const [startDate, endDate] = payload;
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        startDate,
        endDate,
      });
      state.currentPage = initialState.currentPage;
    },
    setSearchFilter: (state, { payload }: PayloadAction<string>) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        searchQuery: payload === '' ? undefined : payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCompanyIdsFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        companyIds?: string[];
        excludeCompanyIds?: string[];
      }>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        companyIds: payload.companyIds,
        excludeCompanyIds: payload.excludeCompanyIds,
      });
      state.currentPage = initialState.currentPage;
    },
    setEventTypeFilter: (
      state,
      { payload }: PayloadAction<EventTypesEnum[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        eventType: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setTagIdsFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        tagIds: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setShippingProfilesFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        shippingProfiles: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setResultsPerPage: (state, { payload }: PayloadAction<number>) => {
      state.resultsPerPage = payload;
      state.currentPage = 0;
    },
    resetPage: (state) => {
      state.selectedFilters = initialState.selectedFilters;
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
    toggleViewAsCustomer: (state) => {
      state.viewAsCustomer = !state.viewAsCustomer;
      persistViewAsCustomer(state.viewAsCustomer);
    },
    setShipmentsForJourney: (
      state,
      {
        payload,
      }: PayloadAction<{ journey: string; shipments: ShipmentEntity[] }>
    ) => {
      state.shipmentsByJourney = {
        ...state.shipmentsByJourney,
        [payload.journey]: payload.shipments,
      };
    },
    setHoveredSnapshot: (
      state,
      { payload }: PayloadAction<{ hovered: HoveredSnapshot | null }>
    ) => {
      state.hoveredSnapshot = payload.hovered;
    },
    setArtycSnapshotsMode: (
      state,
      { payload }: PayloadAction<ArtycSnapshotMode>
    ) => {
      state.artycSnapshotsMode = payload;
    },
  },
});

export const loadJourneys =
  (eventTypes?: EventTypesEnum[]): LoadDataAction<JourneyEntity> =>
  async (
    axios: AxiosInstance,
    auth: Auth,
    state: EntityState<JourneyEntity>
  ) => {
    const {
      resultsPerPage,
      currentPage,
      sortColumn,
      sortDirection,
      selectedFilters,
    } = state;

    const filtersWithEventType = eventTypes
      ? { ...selectedFilters, eventType: eventTypes }
      : selectedFilters;

    const { entities, totalPages } = await structuredFetch<JourneyEntity>(
      axios,
      auth,
      'journeys',
      resultsPerPage,
      currentPage,
      sortColumn,
      sortDirection,
      filtersWithEventType
    );

    return setJourneys({ entities, totalPages });
  };

export const {
  setJourneys,
  sortJourneys,
  setDeviceTypesFilter,
  setSearchFilter,
  setCompanyIdsFilter,
  setEventTypeFilter,
  setTagIdsFilter,
  setShippingProfilesFilter,
  setDateFilter,
  setCurrentPage,
  setResultsPerPage,
  resetPage,
  toggleViewAsCustomer,
  setShipmentsForJourney,
  setHoveredSnapshot,
  setArtycSnapshotsMode,
} = journeySlice.actions;

export default journeySlice.reducer;
